import { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import i18n, { ensureI18nLanguageReady } from '@/lib/i18n-setup';
import { LANGUAGE_LABELS, formatCurrency, type Language, type Currency, type TranslationKey } from '@/lib/i18n';
import { supabase } from '@/integrations/supabase/client';
import { useExchangeRates, convertAmount } from '@/hooks/useExchangeRates';

const LANG_STORAGE_KEY = 'inkvaluator_lang';
const CURRENCY_STORAGE_KEY = 'inkvaluator_currency';
const DISPLAY_CURRENCY_STORAGE_KEY = 'inkvaluator_display_currency';

// Map URL language prefix to default display currency
const LANG_CURRENCY_MAP: Partial<Record<Language, Currency>> = {
  en: 'USD',
  nl: 'EUR',
  de: 'EUR',
  fr: 'EUR',
  es: 'EUR',
  it: 'EUR',
  pt: 'EUR',
  sv: 'SEK',
  pl: 'PLN',
  ja: 'JPY',
  ko: 'KRW',
  ru: 'EUR',
  tr: 'TRY',
  da: 'EUR',
  no: 'NOK',
  fi: 'EUR',
  cs: 'EUR',
};

function getPersistedCurrency(): Currency | null {
  try {
    const stored = localStorage.getItem(CURRENCY_STORAGE_KEY);
    if (stored) return stored as Currency;
  } catch {}
  return null;
}

function getPersistedDisplayCurrency(): Currency | null {
  try {
    const stored = localStorage.getItem(DISPLAY_CURRENCY_STORAGE_KEY);
    if (stored) return stored as Currency;
  } catch {}
  return null;
}

interface ConvertedPrice {
  formatted: string;
  original: number;
  originalCurrency: Currency;
  converted: number;
  displayCurrency: Currency;
  rateDate: string;
  isApprox: boolean;
}

interface I18nContextType {
  language: Language;
  currency: Currency;                 // artist's base currency (data integrity)
  displayCurrency: Currency;          // viewer's chosen display currency
  setLanguage: (lang: Language) => void;
  setCurrency: (currency: Currency) => void;
  setDisplayCurrency: (currency: Currency) => void;
  t: (key: TranslationKey) => string;
  /** Convert + format a price from any source currency to the viewer's display currency */
  convertAndFormat: (amount: number, fromCurrency?: Currency) => ConvertedPrice;
  /** Convert + format a price range */
  convertAndFormatRange: (min: number, max: number, fromCurrency?: Currency) => ConvertedPrice & { formattedMax: string; convertedMax: number };
  rateDate: string | null;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export function I18nProvider({
  children,
  defaultLanguage,
  defaultCurrency = 'EUR',
}: {
  children: ReactNode;
  defaultLanguage?: Language;
  defaultCurrency?: Currency;
}) {
  const { t: i18nT, i18n: i18nInstance } = useTranslation();
  // Artist's base currency (data integrity). Seed once from prop or persisted value.
  const resolvedCurrency = defaultCurrency || getPersistedCurrency() || 'EUR';
  const [currency, setCurrencyState] = useState<Currency>(resolvedCurrency);

  // Display currency seed priority: explicit user choice > artist default > language map
  const initialLang = (defaultLanguage || (i18nInstance.language as Language) || 'en') as Language;
  const persistedDisplay = getPersistedDisplayCurrency();
  const initialDisplay =
    persistedDisplay || defaultCurrency || LANG_CURRENCY_MAP[initialLang] || resolvedCurrency;
  const [displayCurrency, setDisplayCurrencyState] = useState<Currency>(initialDisplay);

  const { data: exchangeRates } = useExchangeRates();

  // One-time sync when artist context changes (slug navigation), but only
  // if the user has NOT explicitly chosen a language. Persisted user choice always wins.
  useEffect(() => {
    if (!defaultLanguage) return;
    const userChose = (() => {
      try { return !!localStorage.getItem(LANG_STORAGE_KEY); } catch { return false; }
    })();
    if (userChose) return;
    if (defaultLanguage !== i18nInstance.language) {
      void ensureI18nLanguageReady(defaultLanguage).finally(() => {
        i18nInstance.changeLanguage(defaultLanguage);
      });
    }
  }, [defaultLanguage, i18nInstance]);

  // Sync artist base currency (used for data writes), but never overwrite the
  // viewer's explicit display currency.
  useEffect(() => {
    if (defaultCurrency) setCurrencyState(defaultCurrency);
  }, [defaultCurrency]);

  // When language changes and the user hasn't explicitly chosen a display currency,
  // follow the language-currency map.
  useEffect(() => {
    const explicit = getPersistedDisplayCurrency();
    if (explicit) return;
    const lang = (i18nInstance.language as Language) || 'en';
    const mapped = LANG_CURRENCY_MAP[lang];
    if (mapped) setDisplayCurrencyState(mapped);
  }, [i18nInstance.language]);

  const setLanguage = useCallback((lang: Language) => {
    void ensureI18nLanguageReady(lang).finally(() => {
      i18nInstance.changeLanguage(lang);
      try { localStorage.setItem(LANG_STORAGE_KEY, lang); } catch {}
    });
  }, [i18nInstance]);

  const setCurrency = useCallback((cur: Currency) => {
    setCurrencyState(cur);
    try { localStorage.setItem(CURRENCY_STORAGE_KEY, cur); } catch {}
  }, []);

  const setDisplayCurrency = useCallback((cur: Currency) => {
    setDisplayCurrencyState(cur);
    try { localStorage.setItem(DISPLAY_CURRENCY_STORAGE_KEY, cur); } catch {}
  }, []);

  useEffect(() => {
    if (defaultLanguage) return;
    // Respect explicit user choice — never overwrite from profile if user picked a language.
    const userChoseLang = (() => {
      try { return !!localStorage.getItem(LANG_STORAGE_KEY); } catch { return false; }
    })();
    const userChoseCurrency = (() => {
      try { return !!localStorage.getItem(CURRENCY_STORAGE_KEY); } catch { return false; }
    })();
    if (userChoseLang && userChoseCurrency) return;
    const syncFromProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile } = await supabase
        .from('artist_profiles')
        .select('language, currency')
        .eq('user_id', user.id)
        .maybeSingle();
      if (!userChoseLang && profile?.language && profile.language in LANGUAGE_LABELS) {
        setLanguage(profile.language as Language);
      }
      if (!userChoseCurrency && profile?.currency) {
        setCurrency(profile.currency as Currency);
      }
    };
    syncFromProfile();
  }, [defaultLanguage, setLanguage, setCurrency]);

  const t = useCallback((key: TranslationKey): string => {
    // Prefer loaded namespace bundles first (DB/static), then fall back to the
    // legacy inline 'translation' bundle from src/lib/i18n.ts.
    return i18nT(key, {
      ns: ['common', 'landing', 'calculator', 'dashboard', 'discover', 'blog', 'city', 'studio', 'admin', 'translation'],
      defaultValue: key,
    });
  }, [i18nT]);

  const language = (i18nInstance.language || 'en') as Language;

  const convertAndFormat = useCallback(
    (amount: number, fromCurrency?: Currency): ConvertedPrice => {
      const from = fromCurrency || currency;
      const rates = exchangeRates?.rates ?? { EUR: 1 };
      const converted = convertAmount(amount, from, displayCurrency, rates);
      // Round to nearest 5 for nicer-looking conversions when not yen/won
      const rounded = ['JPY', 'KRW'].includes(displayCurrency)
        ? Math.round(converted)
        : Math.round(converted / 5) * 5;
      return {
        formatted: formatCurrency(rounded, displayCurrency),
        original: amount,
        originalCurrency: from,
        converted: rounded,
        displayCurrency,
        rateDate: exchangeRates?.rate_date ?? '',
        isApprox: from !== displayCurrency,
      };
    },
    [currency, displayCurrency, exchangeRates],
  );

  const convertAndFormatRange = useCallback(
    (min: number, max: number, fromCurrency?: Currency) => {
      const minResult = convertAndFormat(min, fromCurrency);
      const maxResult = convertAndFormat(max, fromCurrency);
      return {
        ...minResult,
        formatted: minResult.converted === maxResult.converted
          ? minResult.formatted
          : `${minResult.formatted} – ${maxResult.formatted}`,
        formattedMax: maxResult.formatted,
        convertedMax: maxResult.converted,
      };
    },
    [convertAndFormat],
  );

  const value = useMemo<I18nContextType>(() => ({
    language,
    currency,
    displayCurrency,
    setLanguage,
    setCurrency,
    setDisplayCurrency,
    t,
    convertAndFormat,
    convertAndFormatRange,
    rateDate: exchangeRates?.rate_date ?? null,
  }), [language, currency, displayCurrency, setLanguage, setCurrency, setDisplayCurrency, t, convertAndFormat, convertAndFormatRange, exchangeRates]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
}
